import { useState, useEffect } from 'react';
import { MapPin, Plus, Trash2, Edit, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function AddressesPage() {
  const { user, loading: authLoading } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    is_default: false
  });

  const fetchAddresses = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/addresses`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAddresses(data);
      }
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      fetchAddresses();
    }
  }, [authLoading, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingId
        ? `${API}/addresses/${editingId}`
        : `${API}/addresses`;

      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save address');

      toast.success(editingId ? 'Address updated' : 'Address added');
      await fetchAddresses();
      resetForm();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (addressId) => {
    if (!confirm('Are you sure you want to delete this address?')) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/addresses/${addressId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete address');

      toast.success('Address deleted');
      await fetchAddresses();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (address) => {
    setFormData({
      full_name: address.full_name,
      phone: address.phone,
      address_line1: address.address_line1,
      address_line2: address.address_line2 || '',
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      is_default: address.is_default
    });
    setEditingId(address.address_id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      is_default: false
    });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div className="bg-white rounded-3xl p-6 md:p-8" data-testid="addresses-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl text-secondary">Saved Addresses</h1>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="rounded-full bg-primary"
          data-testid="add-address-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New
        </Button>
      </div>

      {/* Address Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-surface rounded-2xl p-6 mb-6 space-y-4">
          <h3 className="font-heading text-lg text-secondary mb-4">
            {editingId ? 'Edit Address' : 'Add New Address'}
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="font-body">Full Name</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="font-body">Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="mt-1 rounded-xl"
              />
            </div>
          </div>

          <div>
            <Label className="font-body">Address Line 1</Label>
            <Input
              value={formData.address_line1}
              onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
              required
              className="mt-1 rounded-xl"
            />
          </div>

          <div>
            <Label className="font-body">Address Line 2 (Optional)</Label>
            <Input
              value={formData.address_line2}
              onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
              className="mt-1 rounded-xl"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="font-body">City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="font-body">State</Label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                required
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="font-body">Pincode</Label>
              <Input
                value={formData.pincode}
                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                required
                className="mt-1 rounded-xl"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_default"
              checked={formData.is_default}
              onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="is_default" className="font-body text-sm cursor-pointer">
              Set as default address
            </Label>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={loading} className="rounded-full bg-primary">
              {loading ? 'Saving...' : (editingId ? 'Update Address' : 'Save Address')}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm} className="rounded-full">
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Addresses List */}
      {addresses.length === 0 && !showForm ? (
        <div className="text-center py-12">
          <MapPin className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="font-heading text-xl text-secondary mb-2">No Addresses Saved</h2>
          <p className="text-muted-foreground font-body mb-6">
            Add your delivery addresses for faster checkout
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {addresses.map((addr) => (
            <div
              key={addr.address_id}
              className="border border-gray-100 rounded-2xl p-4 md:p-6 hover:shadow-soft transition-shadow"
              data-testid={`address-${addr.address_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="font-body font-semibold text-secondary">{addr.full_name}</p>
                    {addr.is_default && (
                      <span className="flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-full font-body">
                        <Check className="w-3 h-3" />
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground font-body">
                    {addr.address_line1}
                    {addr.address_line2 && `, ${addr.address_line2}`}
                  </p>
                  <p className="text-sm text-muted-foreground font-body">
                    {addr.city}, {addr.state} - {addr.pincode}
                  </p>
                  <p className="text-sm text-muted-foreground font-body mt-1">
                    Phone: {addr.phone}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(addr)}
                    className="p-2 hover:bg-surface rounded-full transition-colors"
                  >
                    <Edit className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => handleDelete(addr.address_id)}
                    className="p-2 hover:bg-destructive/10 rounded-full transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
